import { datadogLogs } from '@datadog/browser-logs'

type AuthErrorStep =
  'FORGOT_PASSWORD_REQUEST' | 'FORGOT_PASSWORD_SUBMIT' | 'SIGNUP_CONFIRMATION_SUBMIT'

// Whether the code that failed came from the emailed link or was typed by hand.
// A link code failing points at the link being mangled in transit; a typed one
// does not, so the two must not be conflated.
export type CodeSource = 'link' | 'typed' | 'none'

export const getCodeSource = (submittedCode?: string, linkToken?: string): CodeSource => {
  if (!submittedCode) return 'none'
  return linkToken && submittedCode === linkToken ? 'link' : 'typed'
}

// Cognito is called straight from the browser, so a failed reset leaves no trace
// in our backend logs — without this the only record is a screenshot from the
// customer. `__type` is the field that distinguishes a rejected code from a
// blocked request, which is what SD-3409 lacked. Never send the code or password.
export const reportAuthError = (
  step: AuthErrorStep,
  error: unknown,
  context: { codeSource?: CodeSource } = {},
) => {
  const { __type, code, name } = (error ?? {}) as Record<string, unknown>

  // Only ever report an exception *name*. Amplify also uses `code` for the
  // exception name, but a bare value there could be anything, so anything that
  // isn't clearly a name is dropped rather than risk forwarding a secret.
  // The error's free-text `message` is deliberately not sent at all: Cognito
  // controls its wording, and the exception name already answers the diagnostic
  // question (rejected code vs. request that never arrived).
  const errorName = [__type, code, name].find(
    (value) => typeof value === 'string' && /^[A-Za-z][A-Za-z0-9_]*$/.test(value),
  )

  datadogLogs.logger.error(`Auth failure: ${step}`, {
    step,
    cognitoErrorType: errorName ?? 'UNKNOWN',
    codeSource: context.codeSource,
  })
}
